/**
 * API client functions for study configuration (navigation and modules).
 */

import { api } from './api'

// =============================================================================
// Types
// =============================================================================

export interface NavigationItem {
  id: number
  parentId: number | null
  name: string
  slug: string
  icon: string | null
  sortOrder: number
  isActive: boolean
  requiresStudy: boolean
  navType: 'main' | 'study'
  children: NavigationItem[]
}

export interface ModuleResponse {
  id: number
  moduleKey: string
  displayName: string
  moduleType: string
  category: string | null
  icon: string | null
  description: string | null
  defaultConfig: Record<string, unknown>
  requiredDatasets: string[]
  isActive: boolean
  navigationId: number | null
}

export interface GridLayoutConfig {
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
}

export interface StudyModuleConfig {
  id: number
  moduleId: number
  moduleKey: string
  displayName: string
  moduleType: string
  icon: string | null
  isEnabled: boolean
  customConfig: Record<string, unknown>
  pagePlacement: string | null
  position: number
}

export interface StudyNavigationResponse {
  mainNavigation: NavigationItem[]
  studyNavigation: NavigationItem[]
  modules: StudyModuleConfig[]
}

export interface MultiInstanceConfig {
  id: number
  moduleId: number
  moduleKey: string
  displayName: string
  moduleType: string
  icon: string | null
  isEnabled: boolean
  customConfig: Record<string, unknown>
  pagePlacement: string | null
  position: number
}

export interface AvailableModulesResponse {
  modules: ModuleResponse[]
  studyConfigs: Record<number, StudyModuleConfig>
  multiInstanceConfigs?: MultiInstanceConfig[]
}

export interface PageWidgetsResponse {
  pageKey: string
  widgets: StudyModuleConfig[]
}

export interface ModuleConfigCreate {
  moduleId: number
  isEnabled?: boolean
  customConfig?: Record<string, unknown>
  pagePlacement?: string | null
  position?: number
  gridLayout?: GridLayoutConfig | null
}

export interface ModuleConfigUpdate {
  isEnabled?: boolean
  customConfig?: Record<string, unknown>
  pagePlacement?: string | null
  position?: number
  gridLayout?: GridLayoutConfig | null
}

export interface PageWidgetConfig {
  moduleId: number
  position?: number
  gridLayout?: GridLayoutConfig | null
  customConfig?: Record<string, unknown>
}

// =============================================================================
// Navigation API
// =============================================================================

/**
 * Get main application navigation (no study context).
 */
export async function fetchMainNavigation(): Promise<NavigationItem[]> {
  const response = await api.get<NavigationItem[]>('/config/navigation')
  return response.data
}

/**
 * Get study navigation template.
 */
export async function fetchStudyNavigationTemplate(): Promise<NavigationItem[]> {
  const response = await api.get<NavigationItem[]>('/config/navigation/study')
  return response.data
}

// =============================================================================
// Module API
// =============================================================================

/**
 * Get all available modules.
 */
export async function fetchModules(moduleType?: string): Promise<ModuleResponse[]> {
  const params = moduleType ? { moduleType } : undefined
  const response = await api.get<ModuleResponse[]>('/config/modules', { params })
  return response.data
}

/**
 * Get a specific module by ID.
 */
export async function fetchModule(moduleId: number): Promise<ModuleResponse> {
  const response = await api.get<ModuleResponse>(`/config/modules/${moduleId}`)
  return response.data
}

// =============================================================================
// Study Configuration API
// =============================================================================

/**
 * Get complete navigation configuration for a study.
 */
export async function fetchStudyNavigation(studyId: string): Promise<StudyNavigationResponse> {
  const response = await api.get<StudyNavigationResponse>(`/config/studies/${studyId}/navigation`)
  return response.data
}

/**
 * Get all modules with their study-specific configuration.
 */
export async function fetchStudyModules(studyId: string): Promise<AvailableModulesResponse> {
  const response = await api.get<AvailableModulesResponse>(`/config/studies/${studyId}/modules`)
  return response.data
}

/**
 * Bulk update module configurations for a study.
 */
export async function updateStudyModules(
  studyId: string,
  configs: ModuleConfigCreate[]
): Promise<StudyModuleConfig[]> {
  const response = await api.put<StudyModuleConfig[]>(`/config/studies/${studyId}/modules`, {
    configs,
  })
  return response.data
}

/**
 * Get configuration for a specific module in a study.
 */
export async function fetchModuleConfig(
  studyId: string,
  moduleId: number
): Promise<StudyModuleConfig> {
  const response = await api.get<StudyModuleConfig>(
    `/config/studies/${studyId}/modules/${moduleId}`
  )
  return response.data
}

/**
 * Update configuration for a specific module in a study.
 */
export async function updateModuleConfig(
  studyId: string,
  moduleId: number,
  data: ModuleConfigUpdate
): Promise<StudyModuleConfig> {
  const response = await api.put<StudyModuleConfig>(
    `/config/studies/${studyId}/modules/${moduleId}`,
    data
  )
  return response.data
}

// =============================================================================
// Page Widget API
// =============================================================================

/**
 * Get widgets configured for a specific page.
 */
export async function fetchPageWidgets(
  studyId: string,
  pageKey: string
): Promise<PageWidgetsResponse> {
  const response = await api.get<PageWidgetsResponse>(
    `/config/studies/${studyId}/pages/${pageKey}/widgets`
  )
  return response.data
}

/**
 * Update widget placement for a specific page.
 */
export async function updatePageWidgets(
  studyId: string,
  pageKey: string,
  widgets: PageWidgetConfig[]
): Promise<PageWidgetsResponse> {
  const response = await api.put<PageWidgetsResponse>(
    `/config/studies/${studyId}/pages/${pageKey}/widgets`,
    { widgets }
  )
  return response.data
}

// =============================================================================
// Study Module Initialization
// =============================================================================

/**
 * Initialize default module configurations for a study.
 */
export async function initializeStudyModules(studyId: string): Promise<StudyModuleConfig[]> {
  const response = await api.post<StudyModuleConfig[]>(
    `/config/studies/${studyId}/modules/initialize`
  )
  return response.data
}

// =============================================================================
// Study Pages API
// =============================================================================

export interface StudyPage {
  id: number
  studyId: string
  pageKey: string
  name: string
  description: string | null
  icon: string | null
  sortOrder: number
  isActive: boolean
  parentId: number | null
  navType: 'menu' | 'page' | 'section'
  routePath: string | null
  requiredFeature: string | null
  isSystem: boolean
  color: string | null
  aiCapabilities: string[]
  createdAt: string
  updatedAt: string
  moduleCount: number
  children: StudyPage[]
}

export interface StudyPageCreate {
  pageKey: string
  name: string
  description?: string | null
  icon?: string | null
  sortOrder?: number
  parentId?: number | null
  navType?: 'menu' | 'page' | 'section'
  routePath?: string | null
  requiredFeature?: string | null
  color?: string | null
  aiCapabilities?: string[]
}

export interface StudyPageUpdate {
  name?: string
  description?: string | null
  icon?: string | null
  sortOrder?: number
  isActive?: boolean
  parentId?: number | null
  navType?: 'menu' | 'page' | 'section'
  routePath?: string | null
  requiredFeature?: string | null
  color?: string | null
  aiCapabilities?: string[]
}

export interface PageModulePlacement {
  id: number
  pageId: number
  studyModuleConfigId: number
  instanceId: string
  gridX: number
  gridY: number
  gridW: number
  gridH: number
  moduleKey: string | null
  moduleDisplayName: string | null
  customConfig: Record<string, unknown>
}

export interface PageModulePlacementCreate {
  moduleKey: string
  instanceId: string
  configId?: number
  gridX?: number
  gridY?: number
  gridW?: number
  gridH?: number
  customConfig?: Record<string, unknown>
}

export interface PageLayoutUpdate {
  placements: PageModulePlacementCreate[]
}

/**
 * Get all pages for a study.
 */
export async function fetchStudyPages(studyId: string): Promise<StudyPage[]> {
  const response = await api.get<StudyPage[]>(`/wizard/studies/${studyId}/pages`)
  return response.data
}

/**
 * Create a new page for a study.
 */
export async function createStudyPage(
  studyId: string,
  data: StudyPageCreate
): Promise<StudyPage> {
  // Convert camelCase to snake_case for backend
  const snakeCaseData: Record<string, unknown> = {
    page_key: data.pageKey,
    name: data.name,
    description: data.description,
    icon: data.icon,
    sort_order: data.sortOrder ?? 0,
    parent_id: data.parentId,
    nav_type: data.navType ?? 'page',
    route_path: data.routePath,
    required_feature: data.requiredFeature,
    color: data.color,
  }
  if (data.aiCapabilities !== undefined) snakeCaseData.ai_capabilities = data.aiCapabilities
  const response = await api.post<StudyPage>(`/wizard/studies/${studyId}/pages`, snakeCaseData)
  return response.data
}

/**
 * Get a specific page by key.
 */
export async function fetchStudyPage(
  studyId: string,
  pageKey: string
): Promise<StudyPage> {
  const response = await api.get<StudyPage>(`/wizard/studies/${studyId}/pages/${pageKey}`)
  return response.data
}

/**
 * Update a study page.
 */
export async function updateStudyPage(
  studyId: string,
  pageKey: string,
  data: StudyPageUpdate
): Promise<StudyPage> {
  // Convert camelCase to snake_case for backend (only include defined fields)
  const snakeCaseData: Record<string, unknown> = {}
  if (data.name !== undefined) snakeCaseData.name = data.name
  if (data.description !== undefined) snakeCaseData.description = data.description
  if (data.icon !== undefined) snakeCaseData.icon = data.icon
  if (data.sortOrder !== undefined) snakeCaseData.sort_order = data.sortOrder
  if (data.isActive !== undefined) snakeCaseData.is_active = data.isActive
  if (data.parentId !== undefined) snakeCaseData.parent_id = data.parentId || null  // 0 → null for FK constraint
  if (data.navType !== undefined) snakeCaseData.nav_type = data.navType
  if (data.routePath !== undefined) snakeCaseData.route_path = data.routePath
  if (data.requiredFeature !== undefined) snakeCaseData.required_feature = data.requiredFeature
  if (data.color !== undefined) snakeCaseData.color = data.color
  if (data.aiCapabilities !== undefined) snakeCaseData.ai_capabilities = data.aiCapabilities

  const response = await api.patch<StudyPage>(
    `/wizard/studies/${studyId}/pages/${pageKey}`,
    snakeCaseData
  )
  return response.data
}

/**
 * Delete a study page.
 */
export async function deleteStudyPage(studyId: string, pageKey: string): Promise<void> {
  await api.delete(`/wizard/studies/${studyId}/pages/${pageKey}`)
}

/**
 * Get module placements for a page.
 */
export async function fetchPageLayout(
  studyId: string,
  pageKey: string
): Promise<PageModulePlacement[]> {
  const response = await api.get<PageModulePlacement[]>(
    `/wizard/studies/${studyId}/pages/${pageKey}/layout`
  )
  return response.data
}

/**
 * Update module placements for a page.
 */
export async function updatePageLayout(
  studyId: string,
  pageKey: string,
  data: PageLayoutUpdate
): Promise<PageModulePlacement[]> {
  // Convert camelCase to snake_case for backend
  const snakeCasePlacements = data.placements.map((p) => ({
    module_key: p.moduleKey,
    instance_id: p.instanceId,
    config_id: p.configId ?? null,
    grid_x: p.gridX ?? 0,
    grid_y: p.gridY ?? 0,
    grid_w: p.gridW ?? 6,
    grid_h: p.gridH ?? 4,
    custom_config: p.customConfig ?? {},
  }))
  const response = await api.put<PageModulePlacement[]>(
    `/wizard/studies/${studyId}/pages/${pageKey}/layout`,
    { placements: snakeCasePlacements }
  )
  return response.data
}

// =============================================================================
// Study Navigation Tree API
// =============================================================================

/**
 * Get study navigation tree (hierarchical).
 * Initializes from template if not exists.
 */
export async function fetchStudyNavigationTree(studyId: string): Promise<StudyPage[]> {
  const response = await api.get<StudyPage[]>(`/wizard/studies/${studyId}/navigation`)
  return response.data
}

/**
 * Initialize study navigation from template.
 */
export async function initializeStudyNavigation(studyId: string): Promise<StudyPage[]> {
  const response = await api.post<StudyPage[]>(`/wizard/studies/${studyId}/navigation/initialize`)
  return response.data
}

// =============================================================================
// Page Filter Configuration API
// =============================================================================

export interface PageFilterConfig {
  id: string
  pageId: number
  filterKey: string
  label: string
  filterType: 'multi_select' | 'search'
  sourceDataset: string
  sourceColumn: string
  sortOrder: number
  isEnabled: boolean
  defaultValues: string[] | null
  createdAt: string
  updatedAt: string
}

export interface PageFilterConfigCreate {
  filterKey: string
  label: string
  filterType: 'multi_select' | 'search'
  sourceDataset: string
  sourceColumn: string
  sortOrder?: number
  isEnabled?: boolean
  defaultValues?: string[] | null
}

export interface PageFilterConfigUpdate {
  label?: string
  filterType?: 'multi_select' | 'search'
  sourceDataset?: string
  sourceColumn?: string
  sortOrder?: number
  isEnabled?: boolean
  defaultValues?: string[] | null
}

/**
 * Get all filters configured for a page.
 */
export async function fetchPageFilters(
  studyId: string,
  pageId: number
): Promise<PageFilterConfig[]> {
  const response = await api.get<Record<string, unknown>[]>(
    `/studies/${studyId}/pages/${pageId}/filters`
  )
  return response.data.map(transformPageFilterResponse)
}

/**
 * Create a new page filter.
 */
export async function createPageFilter(
  studyId: string,
  pageId: number,
  data: PageFilterConfigCreate
): Promise<PageFilterConfig> {
  const snakeCaseData = {
    page_id: pageId, // Required by backend schema (even though overridden from URL)
    filter_key: data.filterKey,
    label: data.label,
    filter_type: data.filterType,
    source_dataset: data.sourceDataset,
    source_column: data.sourceColumn,
    sort_order: data.sortOrder ?? 0,
    is_enabled: data.isEnabled ?? true,
    default_values: data.defaultValues ?? null,
  }
  const response = await api.post<Record<string, unknown>>(
    `/studies/${studyId}/pages/${pageId}/filters`,
    snakeCaseData
  )
  return transformPageFilterResponse(response.data)
}

/**
 * Update a page filter.
 */
export async function updatePageFilter(
  studyId: string,
  pageId: number,
  filterId: string,
  data: PageFilterConfigUpdate
): Promise<PageFilterConfig> {
  const snakeCaseData: Record<string, unknown> = {}
  if (data.label !== undefined) snakeCaseData.label = data.label
  if (data.filterType !== undefined) snakeCaseData.filter_type = data.filterType
  if (data.sourceDataset !== undefined) snakeCaseData.source_dataset = data.sourceDataset
  if (data.sourceColumn !== undefined) snakeCaseData.source_column = data.sourceColumn
  if (data.sortOrder !== undefined) snakeCaseData.sort_order = data.sortOrder
  if (data.isEnabled !== undefined) snakeCaseData.is_enabled = data.isEnabled
  if (data.defaultValues !== undefined) snakeCaseData.default_values = data.defaultValues

  const response = await api.put<Record<string, unknown>>(
    `/studies/${studyId}/pages/${pageId}/filters/${filterId}`,
    snakeCaseData
  )
  return transformPageFilterResponse(response.data)
}

/**
 * Delete a page filter.
 */
export async function deletePageFilter(
  studyId: string,
  pageId: number,
  filterId: string
): Promise<void> {
  await api.delete(`/studies/${studyId}/pages/${pageId}/filters/${filterId}`)
}

/**
 * Transform API response to PageFilterConfig type.
 * Note: The API interceptor already transforms snake_case to camelCase,
 * so we just need to cast the data to the correct type.
 */
function transformPageFilterResponse(data: Record<string, unknown>): PageFilterConfig {
  // The API interceptor has already transformed snake_case to camelCase
  // So we access the camelCase keys, not snake_case
  return {
    id: data.id as string,
    pageId: data.pageId as number,
    filterKey: data.filterKey as string,
    label: data.label as string,
    filterType: data.filterType as 'multi_select' | 'search',
    sourceDataset: data.sourceDataset as string,
    sourceColumn: data.sourceColumn as string,
    sortOrder: data.sortOrder as number,
    isEnabled: data.isEnabled as boolean,
    defaultValues: data.defaultValues as string[] | null,
    createdAt: data.createdAt as string,
    updatedAt: data.updatedAt as string,
  }
}
